import { useTranslation } from 'react-i18next';

import { useEffect, useState } from 'react';

import { useGridApiRef } from '@mui/x-data-grid-pro';

import { queries } from 'RestAPIs/rapini';

import { DataGrid } from 'components/shared';
import { THIN_ROW } from 'components/shared/DataGrid';

import { useEmploymentHoursColumns } from './columns/useEmploymentHoursColumns';
import EmploymentHoursToolbar from './components/EmploymentHoursToolbar/EmploymentHoursToolbar';
import HoursDetailDialog from './components/HoursDetailDialog/HoursDetailDialog';
import { useEmploymentHoursData } from './hooks/useEmploymentHoursData';
import { useHoursColumnVisibility } from './hooks/useHoursColumnVisibility';
import { useRecalculateEmploymentHours } from './hooks/useRecalculateEmploymentHours';
import { Container } from './EmploymentHours.styles';
import {
  buildYearOptions,
  snapToNearestValidYear
} from './EmploymentHours.utils';

import type { GridRowId } from '@mui/x-data-grid-pro';
import type {
  EmploymentHoursGridRow,
  EmploymentHoursProps
} from './EmploymentHours.types';

/**
 * Migrated from: frontend/aysist/widgets/roster/employees/EmployeeWorkAgreementsHours/
 *
 * Parity checklist:
 * [x] 12 payroll-period rows for the selected year
 * [x] ~85 columns in 23 compound header groups, driven by the CLA-year flags
 * [x] PLB label substitution and PLB_10_YEARS column swap
 * [x] Dynamic year headers for statutory and extra-legal leave
 * [x] Balance cells: sign flip, colouring, tooltips, WAB warning/alert
 * [x] Totals row (now aligned to its columns, and pinned)
 * [x] Year selector limited to the valid CLA years, snapping to the nearest
 * [x] Recalculate hours, gated by grant; failures now surface (Dojo threw a TypeError instead)
 * [x] Row action: the month detail window, which is also where corrections are made
 * [x] Read-only mode for the two dialog hosts
 * [x] Hours corrections, incl. the last period's balance-budget section
 * [ ] Year corrections dialog — phase 3
 * [ ] Playwright parity run against the Dojo baseline
 */
const EmploymentHours = ({
  employeeId,
  workAgreementId,
  year,
  showYearSelector = true,
  canRecalculateHours = false,
  // the handler below is still the phase-3 placeholder, so an enabled-by-default button would be
  // a dead click for any host that forgets to pass this explicitly
  canViewYearCorrections = false,
  canEditHoursCorrections = false,
  canViewBalanceBudget = false,
  onClaYearLoaded
}: EmploymentHoursProps) => {
  'use memo';

  const { t } = useTranslation('common', { keyPrefix: 'employmentHours' });
  const apiRef = useGridApiRef();
  const [selectedYear, setSelectedYear] = useState(year);

  // employmentId is the work agreement id; see the domain note in CLAUDE.md
  const { data: validYears } = queries.useGetEmploymentClaYearsValid(
    workAgreementId as number,
    { enabled: Boolean(workAgreementId) }
  );

  // The host passes a new year when the selected work agreement changes.
  useEffect(() => {
    if (year !== undefined) {
      setSelectedYear(year);
    }
  }, [year]);

  // Dojo snapped the year inside reloadData (EmployeeWorkAgreementsHours.js:176-186).
  // Snap from the LIVE selection, never from the prop: keying this off `year` would re-snap to the
  // prop's nearest valid year every time the user picked a different one from the dropdown.
  useEffect(() => {
    const snapped = snapToNearestValidYear(selectedYear, validYears);

    if (snapped && snapped !== selectedYear) {
      setSelectedYear(snapped);
    }
  }, [validYears, selectedYear]);

  const {
    gridRows,
    totalsRow,
    hasRows,
    claYear,
    showPlusMinus,
    isLoading,
    isHoursError
  } = useEmploymentHoursData({
    employeeId,
    workAgreementId,
    year: selectedYear
  });

  // Replaces the Dojo `claYearLoaded` event, which the parent used to relabel the
  // balanceBudget tab (EmployeeWorkAgreements.js:286-294).
  useEffect(() => {
    if (claYear) {
      onClaYearLoaded?.(claYear);
    }
  }, [claYear, onClaYearLoaded]);

  const { columns, columnGroupingModel, availableFields } =
    useEmploymentHoursColumns({
      flags: claYear?.flags,
      plbName: claYear?.plbName,
      selectedYear,
      showPlusMinus
    });

  const { columnVisibilityModel, onColumnVisibilityModelChange } =
    useHoursColumnVisibility(availableFields);

  const { mutate: recalculate, isLoading: isRecalculating } =
    useRecalculateEmploymentHours({
      employeeId,
      workAgreementId,
      year: selectedYear
    });

  const [detailRowId, setDetailRowId] = useState<GridRowId | null>(null);

  return (
    <Container>
      <DataGrid<EmploymentHoursGridRow>
        apiRef={apiRef}
        rows={gridRows}
        columns={columns}
        columnGroupingModel={columnGroupingModel}
        columnVisibilityModel={columnVisibilityModel}
        onColumnVisibilityModelChange={onColumnVisibilityModelChange}
        // a real aligned totals row; Dojo's footer was an unaligned text bar below the grid
        pinnedRows={hasRows ? { bottom: [totalsRow] } : undefined}
        initialState={{
          pinnedColumns: {
            left: ['period'],
            right: ['actions']
          }
        }}
        // same dense rows as the hours table next door
        rowHeight={THIN_ROW}
        customActions={(id, row) =>
          row?.isTotals
            ? []
            : [
                {
                  // the pen says the window edits; the list says this month already has corrections
                  icon: !canEditHoursCorrections
                    ? 'eye'
                    : row?.hasCorrections
                    ? 'list-ul'
                    : 'pen',
                  tooltipTitle: t('detail.title'),
                  onClick: () => setDetailRowId(id)
                }
              ]
        }
        loading={isLoading}
        dynamicHeight
        hideFooter
        showToolbar
        slots={{
          toolbar: () => (
            <EmploymentHoursToolbar
              years={buildYearOptions(validYears)}
              selectedYear={selectedYear}
              onYearChange={setSelectedYear}
              showYearSelector={showYearSelector}
              canRecalculateHours={canRecalculateHours}
              canViewYearCorrections={canViewYearCorrections}
              isRecalculating={isRecalculating}
              onRecalculate={recalculate}
              onOpenYearCorrections={() => undefined}
            />
          )
        }}
        getExtraRowClassName={({ row }) =>
          row.isTotals ? 'hours-totals-row' : ''
        }
        translationKey={
          isHoursError ? 'employmentHours.loadError' : 'employmentHours.noHours'
        }
      />
      {detailRowId !== null &&
        hasRows &&
        employeeId &&
        workAgreementId &&
        selectedYear && (
          <HoursDetailDialog
            rows={gridRows}
            rowId={detailRowId}
            employeeId={employeeId}
            workAgreementId={workAgreementId}
            year={selectedYear}
            claYear={claYear}
            showPlusMinus={showPlusMinus}
            canEdit={canEditHoursCorrections}
            canViewBalanceBudget={canViewBalanceBudget}
            onClose={() => setDetailRowId(null)}
          />
        )}
    </Container>
  );
};

export default EmploymentHours;
